import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingTagType,
} from "@lexical/rich-text";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import { Button, Divider, Dropdown, Flex, MenuProps } from "antd";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  BaseSelection,
  COMMAND_PRIORITY_LOW,
  ElementFormatType,
  ElementNode,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  LexicalNode,
  SELECTION_CHANGE_COMMAND,
  TextFormatType,
} from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaParagraph } from "react-icons/fa";
import {
  TbAlignCenter,
  TbAlignLeft,
  TbAlignRight,
  TbBold,
  TbBrandYoutube,
  TbChevronDown,
  TbH1,
  TbH2,
  TbH3,
  TbItalic,
  TbLink,
  TbList,
  TbListNumbers,
  TbMicrophone,
  TbPhoto,
  TbQuote,
  TbStrikethrough,
  TbUnderline,
} from "react-icons/tb";
import { useToken } from "../../../hooks/useToken";
import { useTheme } from "../../../providers/ThemeProvider";
import { improveWriting } from "../../../services/ai.service";

const LowPriority = COMMAND_PRIORITY_LOW;
const blockTypeToDisplay: { [key: string]: string } = {
  paragraph: "Paragraph",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  quote: "Quote",
  ul: "Bulleted List",
  ol: "Numbered List",
  "code-block": "Code Block",
};

function getSelectedNode(selection: BaseSelection | null): LexicalNode | null {
  if (!selection || !$isRangeSelection(selection)) {
    return null;
  }
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (!anchorNode || !focusNode) {
    return null;
  }
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const topLevelElement = anchorNode.getTopLevelElement();
  return topLevelElement ?? anchorNode;
}

function $setBlocksType(
  editor: LexicalEditor,
  selection: BaseSelection | any,
  getType: () => ElementNode
): void {
  if (!$isRangeSelection(selection)) {
    editor.update(() => {
      const anchorNode = selection?.anchor.getNode();
      const element =
        anchorNode?.getTopLevelElement() ?? editor.getRootElement();
      if (element) {
        const newBlock = getType();
        selection?.insertNodes([newBlock]);
        if (newBlock.isAttached()) {
          newBlock.selectEnd();
        }
      }
    });
    return;
  }
  editor.update(() => {
    const nodes = selection.getNodes();
    if (nodes.length === 0) {
      const newBlock = getType();
      selection.insertNodes([newBlock]);
      if (newBlock.isAttached()) {
        newBlock.selectEnd();
      }
      return;
    }
    const topLevelElements = new Set<ElementNode>();
    nodes.forEach((node) => {
      const topLevel = node.getTopLevelElement();
      if ($isElementNode(topLevel) && !topLevel.isInline()) {
        topLevelElements.add(topLevel);
      }
    });
    let firstNewBlock: ElementNode | any = null;
    topLevelElements.forEach((element) => {
      const newBlock = getType();
      try {
        element.replace(newBlock);
        if (!firstNewBlock) {
          firstNewBlock = newBlock;
        }
      } catch (e) {
        console.error("Error replacing element in $setBlocksType:", e, element);
        try {
          element.insertAfter(newBlock);
        } catch (insertError) {
          console.error("Error inserting after element:", insertError);
        }
      }
    });
    if (firstNewBlock && firstNewBlock.isAttached()) {
      firstNewBlock.selectStart();
    }
  });
}

interface ToolbarPluginProps {}

export default function ToolbarPluginTeste({}: ToolbarPluginProps) {
  const { token } = useToken();
  const { theme } = useTheme();
  const [isImprovingWriting, setImprovingWriting] = useState(false);

  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [blockType, setBlockType] = useState<string>("paragraph");
  const [selectedElementKey, setSelectedElementKey] = useState<string | null>(
    null
  );
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [elementFormat, setElementFormat] = useState<ElementFormatType>("left");

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element = anchorNode.getTopLevelElement();
      if (element === editor.getRootElement() || !$isElementNode(element)) {
        element = anchorNode.getParent();
      }
      if (element && $isElementNode(element) && !element.isInline()) {
        const elementKey = element.getKey();
        setSelectedElementKey(elementKey);
        setElementFormat(element.getFormatType());
        setIsBold(selection.hasFormat("bold"));
        setIsItalic(selection.hasFormat("italic"));
        setIsUnderline(selection.hasFormat("underline"));
        setIsStrikethrough(selection.hasFormat("strikethrough"));
        const node = getSelectedNode(selection);
        const parent = node?.getParent();
        setIsLink($isLinkNode(parent) || $isLinkNode(node));
        const nearestList = $getNearestNodeOfType(anchorNode, ListNode);
        if (nearestList) {
          setBlockType(nearestList.getTag());
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
        }
      } else {
        setBlockType("paragraph");
        setIsLink(false);
        setIsBold(false);
        setIsItalic(false);
        setIsUnderline(false);
        setIsStrikethrough(false);
        setElementFormat("left");
        setSelectedElementKey(null);
      }
    } else {
      setBlockType("paragraph");
      setIsLink(false);
      setIsBold(false);
      setIsItalic(false);
      setIsUnderline(false);
      setIsStrikethrough(false);
      setElementFormat("left");
      setSelectedElementKey(null);
    }
  }, [editor]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateToolbar();
    });
    const unregister = mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateToolbar();
          return false;
        },
        LowPriority
      )
    );
    return () => {
      unregister();
    };
  }, [editor, $updateToolbar]);

  const formatText = useCallback(
    (format: TextFormatType) => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    },
    [editor]
  );
  const formatElement = useCallback(
    (format: ElementFormatType) => {
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
    },
    [editor]
  );
  const formatParagraph = useCallback(() => {
    editor.update(() => {
      $setBlocksType(editor, $getSelection(), $createParagraphNode);
    });
  }, [editor]);
  const formatHeading = useCallback(
    (tag: HeadingTagType) => {
      editor.update(() => {
        $setBlocksType(editor, $getSelection(), () => $createHeadingNode(tag));
      });
    },
    [editor]
  );
  const formatQuote = useCallback(() => {
    editor.update(() => {
      $setBlocksType(editor, $getSelection(), $createQuoteNode);
    });
  }, [editor]);
  const formatList = useCallback(
    (listType: "ul" | "ol") => {
      const command =
        listType === "ul"
          ? INSERT_UNORDERED_LIST_COMMAND
          : INSERT_ORDERED_LIST_COMMAND;
      if (blockType !== listType) {
        editor.dispatchCommand(command, undefined);
      } else {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      }
    },
    [editor, blockType]
  );
  const insertLink = useCallback(() => {
    let url: string | null = null;
    if (!isLink) {
      url = prompt("Enter the URL:");
      if (url === null || url.trim() === "") return;
    }
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
  }, [editor, isLink]);

  const blockTypeItems: MenuProps["items"] = [
    {
      key: "paragraph",
      label: blockTypeToDisplay["paragraph"],
      icon: <FaParagraph />,
      onClick: formatParagraph,
    },
    {
      key: "h1",
      label: blockTypeToDisplay["h1"],
      icon: <TbH1 />,
      onClick: () => formatHeading("h1"),
    },
    {
      key: "h2",
      label: blockTypeToDisplay["h2"],
      icon: <TbH2 />,
      onClick: () => formatHeading("h2"),
    },
    {
      key: "h3",
      label: blockTypeToDisplay["h3"],
      icon: <TbH3 />,
      onClick: () => formatHeading("h3"),
    },
    {
      key: "quote",
      label: blockTypeToDisplay["quote"],
      icon: <TbQuote />,
      onClick: formatQuote,
    },
  ];
  const menuProps = {
    items: blockTypeItems,
    selectedKeys: blockType ? [blockType] : [],
  };
  const getBlockTypeDisplay = () => {
    return blockType && blockTypeToDisplay[blockType]
      ? blockTypeToDisplay[blockType]
      : blockTypeToDisplay["paragraph"];
  };

  return (
    <Flex
      className="toolbar"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "1px",
        padding: `${token.paddingSM}px ${token.padding}px`,
        background:
          theme === "DARK"
            ? "rgba(39, 39, 42, 0.80)"
            : "rgba(255,255,255,0.80)",
        backdropFilter: "blur(20px)",
        borderTopLeftRadius: token.borderRadiusLG,
        borderTopRightRadius: token.borderRadiusLG,
        borderBottom: `1px solid ${token.colorSplit}`,
        height: "56px",
        flexShrink: 0,
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
      ref={toolbarRef}
    >
      <Flex
        align="center"
        gap={token.paddingXXS}
        style={{
          flexGrow: 1,
          overflowX: "auto",
          paddingRight: token.paddingSM,
        }}
      >
        <>
          <Dropdown
            menu={menuProps}
            overlayStyle={{ border: `1px solid ${token.colorBorderSecondary}` }}
            trigger={["click"]}
          >
            <Button
              size="small"
              style={{
                display: "flex",
                alignItems: "center",
                border: "none",
                boxShadow: "none",
                background: "transparent",
                minWidth: 100,
                paddingLeft: 0,
                paddingRight: 0,
              }}
              title="Block Type"
            >
              <span
                style={{
                  flexGrow: 1,
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  marginRight: 4,
                }}
              >
                {getBlockTypeDisplay()}
              </span>
              <TbChevronDown size={16} />
            </Button>
          </Dropdown>
          <Divider
            type="vertical"
            style={{ height: "20px", margin: "0 4px" }}
          />
          <Button
            title="Bold"
            size="small"
            type={isBold ? "primary" : "text"}
            icon={<TbBold size={18} />}
            onClick={() => formatText("bold")}
          />
          <Button
            title="Italic"
            size="small"
            type={isItalic ? "primary" : "text"}
            icon={<TbItalic size={18} />}
            onClick={() => formatText("italic")}
          />
          <Button
            title="Underline"
            size="small"
            type={isUnderline ? "primary" : "text"}
            icon={<TbUnderline size={18} />}
            onClick={() => formatText("underline")}
          />
          <Button
            title="Strikethrough"
            size="small"
            type={isStrikethrough ? "primary" : "text"}
            icon={<TbStrikethrough size={18} />}
            onClick={() => formatText("strikethrough")}
          />
          <Divider
            type="vertical"
            style={{ height: "20px", margin: "0 4px" }}
          />
          <Button
            title="Insert Link"
            size="small"
            type={isLink ? "primary" : "text"}
            icon={<TbLink size={18} />}
            disabled
            onClick={insertLink}
          />
          <Button
            title="Insert Image (Not Implemented)"
            disabled
            size="small"
            type="text"
            icon={<TbPhoto size={18} />}
          />
          <Button
            title="Insert Video (Not Implemented)"
            disabled
            size="small"
            type="text"
            icon={<TbBrandYoutube size={18} />}
          />
          <Button
            title="Voice Input (Not Implemented)"
            disabled
            size="small"
            type="text"
            icon={<TbMicrophone size={18} />}
          />
          <Divider
            type="vertical"
            style={{ height: "20px", margin: "0 4px" }}
          />
          <Button
            title="Bulleted List"
            size="small"
            type={blockType === "ul" ? "primary" : "text"}
            icon={<TbList size={18} />}
            onClick={() => formatList("ul")}
          />
          <Button
            title="Numbered List"
            size="small"
            type={blockType === "ol" ? "primary" : "text"}
            icon={<TbListNumbers size={18} />}
            onClick={() => formatList("ol")}
          />
          <Divider
            type="vertical"
            style={{ height: "20px", margin: "0 4px" }}
          />
          <Button
            title="Left Align"
            size="small"
            type={elementFormat === "left" ? "primary" : "text"}
            icon={<TbAlignLeft size={18} />}
            onClick={() => formatElement("left")}
          />
          <Button
            title="Center Align"
            size="small"
            type={elementFormat === "center" ? "primary" : "text"}
            icon={<TbAlignCenter size={18} />}
            onClick={() => formatElement("center")}
          />
          <Button
            title="Right Align"
            size="small"
            type={elementFormat === "right" ? "primary" : "text"}
            icon={<TbAlignRight size={18} />}
            onClick={() => formatElement("right")}
          />
          <Divider
            type="vertical"
            style={{ height: "20px", margin: "0 4px" }}
          />
          <Flex
            className="gradient-border"
            style={{
              cursor: "pointer",
              padding: "2px",
              alignItems: "center",
              height: "30px",
              marginLeft: "23%",
            }}
            title="Improve Writing"
          >
            <Button
              type="default"
              size="small"
              style={{ height: "100%" }}
              loading={isImprovingWriting}
              onClick={() => {
                if (isImprovingWriting) return;
                setImprovingWriting(true);

                editor.read(() => {
                  const textContent = $getRoot().getTextContent();

                  improveWriting({
                    onSuccess: (data) => {
                      console.log(data.textOutput);

                      editor.update(() => {
                        const root = $getRoot();
                        root.clear();

                        const newParagraph = $createParagraphNode();
                        const textNode = $createTextNode(data.textOutput);
                        newParagraph.append(textNode);
                        root.append(newParagraph);
                      });

                      setImprovingWriting(false);
                    },
                    onError: () => {
                      setImprovingWriting(false);
                    },
                    text: textContent,
                  });
                });
              }}
            >
              Improve writing
            </Button>
          </Flex>
        </>
      </Flex>
    </Flex>
  );
}
